/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

/**
 * Centralized constants for status messages, selectors, and common values
 * Eliminates magic strings and provides consistency across the application
 */

export const StatusMessages = {
  // Loading messages
  LOADING: (operation: string) => `${operation}...`,
  LOADING_NOTE: 'Loading note...',
  LOADING_CONTENT: 'Loading content...',
  LOADING_REVISION: 'Loading revision...',
  
  // Success messages
  SUCCESS: (operation: string) => `${operation} complete.`,
  READY: 'Ready',
  NOTE_LOADED: (title?: string) => title ? `Loaded note: ${title}` : 'Note loaded',
  NOTE_SAVED: 'Note saved',
  TRANSCRIPTION_COMPLETE: 'Transcription complete.',
  POLISHING_COMPLETE: 'Polishing complete.',
  TRANSLATION_COMPLETE: 'Translation complete.',
  
  // Error messages
  ERROR: (operation: string) => `Failed to ${operation.toLowerCase()}.`,
  FAILED_TO_LOAD: 'Failed to load note.',
  FAILED_TO_SAVE: 'Failed to save note.',
  FAILED_TO_TRANSCRIBE: 'Error transcribing audio clip.',
  FAILED_TO_POLISH: 'Failed to polish note.',
  FAILED_TO_TRANSLATE: 'Failed to translate.',
  
  // Progress messages
  PROGRESS: (operation: string, detail: string) => `${operation}: ${detail}`,
  DETECTING_LANGUAGE: (language: string) => `Language: ${language}.`,
  POLISHING_NOTE: (language: string) => `Language: ${language}. Polishing note...`,
  
  // Status messages
  NO_ACTIVE_NOTE: 'No active note to process.',
  NO_CONTENT: 'No content to process.',
  NO_ACTIVE_EDITOR: 'No active editor found.',
  ALL_DATA_CLEARED: 'All local data cleared',
  SYNC_COMPLETE: 'Sync complete! Loading notes...'
} as const;

export const Selectors = {
  // Tab and content selectors
  TAB_BUTTONS: '[data-tab]',
  CONTENT_ELEMENTS: '[data-content]',
  ACTIVE_TAB: '.tab-button.active',
  
  // Editor selectors
  EDITABLE_ELEMENTS: '[contenteditable][placeholder]',
  ACTIVE_EDITOR: '.editor:focus',
  
  // Note elements
  NOTE_TITLE: '#noteTitle',
  RAW_TRANSCRIPTION: '#rawTranscription',
  POLISHED_NOTE: '#polishedNote',
  TRANSLATED_NOTE: '#translatedNote',
  
  // UI elements
  STATUS_BAR: '.status-bar',
  NOTES_LIST: '.notes-list',
  HISTORY_CONTAINER: '.history-pane-content',
  
  // Button selectors
  TRANSCRIBE_BUTTON: '#transcribeButton',
  POLISH_BUTTON: '[data-action="polish"]',
  TRANSLATE_BUTTON: '[data-action="translate"]',
  
  // Modal selectors
  MODAL_OVERLAY: '.modal-overlay',
  MODAL_CONTENT: '.modal-content',
  CONFIRM_DIALOG: '.confirm-dialog'
} as const;

export const ErrorMessages = {
  // Service errors
  SERVICE_NOT_FOUND: (service: string) => `Service "${service}" not found in registry.`,
  SERVICE_ALREADY_REGISTERED: (service: string) => `Service "${service}" is already registered.`,
  
  // Operation errors
  NO_ACTIVE_NOTE: 'No active note available.',
  NO_CONTENT_TO_PROCESS: 'No content available to process.',
  INVALID_EDITOR: 'Invalid or inactive editor.',
  
  // Authentication errors
  AUTH_REQUIRED: 'Authentication required.',
  AUTH_FAILED: 'Authentication failed.',
  TOKEN_EXPIRED: 'Authentication token expired.',
  
  // Network errors
  NETWORK_ERROR: 'Network error occurred.',
  API_ERROR: 'API request failed.',
  TIMEOUT_ERROR: 'Request timed out.',
  
  // Data errors
  DATA_NOT_FOUND: 'Requested data not found.',
  DATA_CORRUPTION: 'Data corruption detected.',
  STORAGE_QUOTA_EXCEEDED: 'Storage quota exceeded.'
} as const;

export const Timeouts = {
  // UI timeouts
  DEBOUNCE_DELAY: 300,
  AUTO_SAVE_DELAY: 1000,
  TOAST_DISPLAY_DURATION: 3000,
  
  // API timeouts
  API_REQUEST_TIMEOUT: 30000,
  TRANSCRIPTION_TIMEOUT: 60000,
  
  // Animation timeouts
  FADE_IN_DURATION: 200,
  FADE_OUT_DURATION: 150,
  SLIDE_DURATION: 300
} as const;

export const Limits = {
  // Content limits
  MAX_NOTE_TITLE_LENGTH: 200,
  MAX_TRANSCRIPTION_LENGTH: 100000,
  MAX_FILE_SIZE_MB: 25,
  
  // UI limits
  MAX_NOTES_IN_LIST: 100,
  MAX_HISTORY_ENTRIES: 50,
  MAX_TOKEN_USAGE_RECORDS: 1000
} as const;
